import React, { Component, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import SearchIcon from '@atlaskit/icon/glyph/search';
import InlineDialog from '@atlaskit/inline-dialog';
import { Text } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { type IntlShape, FormattedMessage, injectIntl } from '@atlassian/jira-intl';
import { SearchField } from '@atlassian/jira-searchfield/src/index.tsx';
import type { Article } from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import Link from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-spa-link/index.tsx';
import type { PublicArticleSearchResultProps } from '../../article-search-result/index.tsx';
import type { SearchKnowledgePublicProps } from '../../search-knowledge/index.tsx';
import messages from './messages.tsx';

type SearchKnowledgeInlineStatelessProps = {
	isOpen: boolean;
	searchScreenRedirectUrl: string;
	activationId?: string;
	value: string;
	SearchKnowledge: ComponentType<SearchKnowledgePublicProps>;
	SearchResultComponent: ComponentType<PublicArticleSearchResultProps>;
	projectId: number;
	placeholder: string;
	onClose: () => void;
	onFieldSearchFocus: () => void;
	onChange: (value: string) => void;
	onSubmit: () => void;
	intl: IntlShape;
};

// Please migrate me to a functional component :)
// eslint-disable-next-line jira/react/no-class-components
export class SearchKnowledgeInlineStateless extends Component<SearchKnowledgeInlineStatelessProps> {
	static defaultProps = {
		value: '',
	};

	renderResult = (article: Article) => {
		const { SearchResultComponent } = this.props;
		return (
			<SearchResultComponent
				id={article.id}
				title={article.titleExcerpt || ''}
				description={article.summaryExcerpt || ''}
				categories={article.categories}
				lineClamp={2}
			/>
		);
	};

	render() {
		const {
			value,
			SearchKnowledge,
			onChange,
			placeholder,
			onSubmit,
			intl,
			isOpen,
			onFieldSearchFocus,
			onClose,
			searchScreenRedirectUrl,
			SearchResultComponent: _,
			activationId,
			...props
		} = this.props;

		return (
			<InlineDialogContainer>
				<InlineDialog
					isOpen={!!value && isOpen}
					onClose={onClose}
					content={
						<ContentContainer>
							<SearchKnowledge
								{...props}
								limit={3}
								value={value}
								renderResult={this.renderResult}
								// @ts-expect-error - Property activationId does not exist
								activationId={activationId ?? ''}
								successCtaComponent={
									searchScreenRedirectUrl ? (
										<LinkToSearchPage to={searchScreenRedirectUrl}>
											<FormattedMessage
												{...messages.searchAllArticlesCta}
												values={{ term: <Text as="strong">{value}</Text> }}
											/>

											<SearchIcon label={intl.formatMessage(messages.searchIconLabel)} />
										</LinkToSearchPage>
									) : null
								}
							/>
						</ContentContainer>
					}
				>
					<FieldContainer>
						<SearchField
							isAlwaysExpanded
							onEnter={onSubmit}
							onFocus={onFieldSearchFocus}
							value={value}
							onChange={onChange}
							placeholder={placeholder}
							shouldFitContainerWidth
							isAutocompleteDisabled
						/>
					</FieldContainer>
				</InlineDialog>
			</InlineDialogContainer>
		);
	}
}

export default injectIntl(SearchKnowledgeInlineStateless);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	width: '320px',
	/* Override white-space inside @atlaskit/inline-dialog */
	whiteSpace: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldContainer = styled.div({
	/*
    This is set to a specific width so the inline dialog hits the edge of the table.
    Don't adjust without confirming what it looks like!
     */
	width: '240px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineDialogContainer = styled.div({
	marginLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkToSearchPage = styled(Link)({
	boxSizing: 'border-box',
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginTop: token('space.200', '16px'),
	marginRight: token('space.negative.300', '-24px'),
	marginBottom: token('space.negative.200', '-16px'),
	marginLeft: token('space.negative.300', '-24px'),
	paddingTop: token('space.200', '16px'),
	paddingRight: token('space.300', '24px'),
	paddingBottom: token('space.200', '16px'),
	paddingLeft: token('space.300', '24px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text', colors.N800)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.neutral', colors.N10)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `0 0 ${borderRadius}px ${borderRadius}px`,
});
